import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthPage } from './components/Auth/AuthPage';
import { Login } from './components/Auth/Login';
import { Register } from './components/Auth/Register';
import { BusinessRegister } from './components/Auth/BusinessRegister';
import { ForgotPassword } from './components/Auth/ForgotPassword';
import WorkerDashboard from './components/WorkerDashboard';
import { useAuth } from './contexts/AuthContext';
import './i18n/config';
import './App.css';
import DynamicIslandMenu from './components/DynamicIslandMenu';
import AdminDashboard from './components/AdminDashboard';
import OnBoardingFlow from './components/OnBoarding/OnBoardingFlow';
import SkillsManagement from './pages/Admin/SkillsManagement';
import { UserPasswordManagement } from './pages/Admin/UserPasswordManagement';
import { logger } from './utils/logger';
import MyCertifications from './pages/MyCertifications';
import Balance from './pages/Balance';
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";
import BusinessDashboard from './pages/BusinessDashboard/BusinessDashboard';
import BusinessRoute from './components/ProtectedRoute/BusinessRoute';
import BusinessOrEmployeeRoute from './components/ProtectedRoute/BusinessOrEmployeeRoute';
import Quotations from './pages/Admin/Quotations';
import PublicQuotationForm from './pages/PublicQuotationForm';
import PublicQuotationFormV2 from './pages/PublicQuotationFormV2';
import ThankYouPage from './pages/ThankYouPage';
import QuotationScreen from './pages/Admin/QuotationScreen';
import MyBusiness from './pages/MyBusiness/MyBusiness';
import EmployeeRegistration from './pages/EmployeeRegistration';
import Projects from './pages/Admin/Projects';
import ProjectView from './pages/Admin/ProjectView';
import ApprovalProcess from './pages/Admin/approvalProcess';
import CertificationMigration from './pages/Admin/CertificationMigration';
import EmployeeDashboard from './components/EmployeeDashboard';
import { EmployeeRegister } from './components/Auth/EmployeeRegister';
import MigrationChecklist from './components/migration';
import WorkerProjects from './pages/WorkerProjects';
import WorkerProjectView from './pages/WorkerProjectView';
import WorkerTaskView from './pages/WorkerTaskView';
import WelcomeScreen from './pages/WelcomeScreen';
import BusinessManagement from './pages/Admin/BusinessManagement';
import Clients from './pages/BusinessDashboard/Clients';
import SiteSettings from './pages/Admin/SiteSettings';
import WorkerRemovalHistory from './pages/Admin/WorkerRemovalHistory';
import MaintenancePopup from './components/MaintenancePopup';
import { useMaintenance } from './contexts/MaintenanceContext';
import TaskHistory from './pages/Admin/TaskHistory';
import ProjectHistory from './pages/Admin/ProjectHistory';
import { HealthBusinessRegister } from './components/Auth/HealthBusinessRegister';
import WorkerAgenda from './pages/WorkerAgenda';
import BusinessAgenda from './pages/BusinessAgenda';
import DynamicCodeTest from './pages/DynamicCodeTest';
import UserBlocked from './pages/UserBlocked';
import WorkerProfile from './pages/WorkerProfile';
import WorkerJobHistory from './pages/WorkerJobHistory';
import WorkerSettings from './pages/WorkerSettings';
import WorkerPublicProfile from './pages/WorkerPublicProfile';
import BlockedUserRoute from './components/ProtectedRoute/BlockedUserRoute';

// Suprimir la advertencia de React DevTools
logger.reinitializeDevToolsWarning();

function getDashboardRouteByRole(role: string, status?: string) {
  // Si el usuario está bloqueado, redirigir a la página de usuario bloqueado
  if (status === 'blocked') return '/user-blocked';
  
  if (role === 'admin') return '/admin-dashboard';
  if (role === 'business') return '/business/dashboard';
  if (role === 'worker' || role === 'User' || role === 'Worker') return '/worker-dashboard';
  if (role === 'employee') return '/employee-dashboard';
  return '/auth/login';
}

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth();
  if (!currentUser) {
    return <Navigate to="/" />;
  }
  
  return (
    <BlockedUserRoute>
      {children}
    </BlockedUserRoute>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  const { currentUser, userData } = useAuth();
  const authHiddenRoutes = [
    '/auth/login',
    '/auth/register',
    '/auth/business-register',
    '/auth/forgot-password',
    '/quotation-form',
    '/thank-you'
  ];
  const hideMenu = authHiddenRoutes.some(route => location.pathname.startsWith(route));
  return (
    <div className="app-container">
      {/* <MaintenancePopup /> */}
      {!hideMenu && <DynamicIslandMenu />}
      <Routes>
        <Route path="/" element={currentUser && userData ? <Navigate to={getDashboardRouteByRole(userData.role, userData.status)} replace /> : <WelcomeScreen />} />
        <Route path="/worker-dashboard" element={<PrivateRoute><WorkerDashboard /></PrivateRoute>} />
        <Route path="/employee-dashboard" element={<PrivateRoute><EmployeeDashboard /></PrivateRoute>} />
        <Route path="/admin-dashboard" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/skills" element={<PrivateRoute><SkillsManagement /></PrivateRoute>} />
        <Route path="/admin/user-passwords" element={<PrivateRoute><UserPasswordManagement /></PrivateRoute>} />
        <Route path="/admin/business-management" element={<PrivateRoute><BusinessManagement /></PrivateRoute>} />
        <Route path="/admin/site-settings" element={<PrivateRoute><SiteSettings /></PrivateRoute>} />
        <Route path="/admin/worker-removal-history" element={<PrivateRoute><WorkerRemovalHistory /></PrivateRoute>} />
        <Route path="/onboarding" element={<PrivateRoute><OnBoardingFlow /></PrivateRoute>} />
        <Route path="/dynamic-code-test" element={<PrivateRoute><DynamicCodeTest /></PrivateRoute>} />
        <Route path="/auth" element={<AuthPage />}>
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="employee-register" element={<EmployeeRegister />} />
          <Route path="business-register" element={<BusinessRegister />} />
          <Route path="health-business-register" element={<HealthBusinessRegister />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
        </Route>
        <Route path="/my-certifications" element={<PrivateRoute><MyCertifications /></PrivateRoute>} />
        <Route path="/balance" element={<PrivateRoute><Balance /></PrivateRoute>} />
        <Route path="/business/dashboard" element={
          <BusinessRoute>
            <BusinessDashboard />
          </BusinessRoute>
        } />
        <Route path="/quotations" element={<PrivateRoute><Quotations /></PrivateRoute>} />
        <Route path="/quotations/:quotationId" element={<PrivateRoute><QuotationScreen /></PrivateRoute>} />
        <Route path="/projects" element={
          <BusinessOrEmployeeRoute>
            <Projects />
          </BusinessOrEmployeeRoute>
        } />
        <Route path="/projects/:projectId" element={<ProjectView />} />
        <Route path="/quotation-form/:businessId" element={<PublicQuotationForm />} />
        <Route path="/quotation-form-v2/:businessId" element={<PublicQuotationFormV2 />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/employee-registration/:businessId" element={<EmployeeRegistration />} />
        <Route path="/business/company" element={
          <BusinessRoute>
            <MyBusiness />
          </BusinessRoute>
        } />
        <Route path="/business/profile" element={
          <BusinessRoute>
            <WorkerProfile />
          </BusinessRoute>
        } />
        <Route path="/admin/approval-process" element={<PrivateRoute><ApprovalProcess /></PrivateRoute>} />
        <Route path="/admin/certification-migration" element={<PrivateRoute><CertificationMigration /></PrivateRoute>} />
        <Route path="/migration" element={<MigrationChecklist />} />
        <Route path="/worker-projects" element={<PrivateRoute><WorkerProjects /></PrivateRoute>} />
        <Route path="/worker-projects/:projectId" element={<PrivateRoute><WorkerProjectView /></PrivateRoute>} />
        <Route path="/worker-projects/:projectId/tasks/:taskId" element={<PrivateRoute><WorkerTaskView /></PrivateRoute>} />
        <Route path="/business/clients" element={
          <BusinessRoute>
            <Clients />
          </BusinessRoute>
        } />
        <Route path="/admin/task-history/:projectId/:taskId" element={<PrivateRoute><TaskHistory /></PrivateRoute>} />
        <Route path="/admin/project-history/:projectId" element={<PrivateRoute><ProjectHistory /></PrivateRoute>} />
        <Route path="/worker-agenda" element={<PrivateRoute><WorkerAgenda /></PrivateRoute>} />
        <Route path="/business-agenda" element={<PrivateRoute><BusinessAgenda /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><WorkerProfile /></PrivateRoute>} />
        <Route path="/worker-job-history" element={<PrivateRoute><WorkerJobHistory /></PrivateRoute>} />
        <Route path="/worker-settings" element={<PrivateRoute><WorkerSettings /></PrivateRoute>} />
        <Route path="/worker/:workerId" element={<WorkerPublicProfile />} />
        <Route path="/user-blocked" element={<UserBlocked />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

const App: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const { maintenance } = useMaintenance();
  useEffect(() => {
    // Asegurar que la supresión de la advertencia se aplique después del montaje
    logger.reinitializeDevToolsWarning();
  }, []);

  // Mostrar el popup si maintenance está activo y:
  // - el usuario está autenticado y no es admin (o el rol aún no está cargado)
  // - o el usuario no está autenticado (opcional, puedes ajustar esto si solo quieres bloquear usuarios logueados)
  const showMaintenance = maintenance && currentUser && (!userData || userData.role !== 'admin');

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {showMaintenance && <MaintenancePopup />}
      <AppRoutes />
    </BrowserRouter>
  );
};

export default App;
